// lib/react-query/bizzQueries.ts
import React from 'react';
import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query";
import { toast } from "@/components/ui/use-toast";
import {
  createVideoPost,
  uploadVideoFile,
  getVideoFeed,
  getUserVideos,
  deleteVideoPost,
  likeVideoPost,
  saveVideoPost,
  shareVideoPost,
  viewVideoPost,
  getVideoStats,
  getUserVideoInteractions,
  createVideoTip,
  updateVideoTipStatus,
  getUserVideoTips,
  searchVideos,
  getTrendingVideos,
   getVideoAnalytics,
  getUserAnalytics,
  getUserLikedVideos,
  getUserSavedVideos,
  getSingleVideo,
  getVideoInteractions,
  getVideoTips,
} from "@/bizz/lib/appwrite/bizzApi";
import { appwriteConfig, databases } from '@/lib/appwrite/config';
import { Query } from 'appwrite';

export const QUERY_KEYS = {
  GET_VIDEO_FEED: "getVideoFeed",
  GET_USER_VIDEOS: "getUserVideos", 
  GET_VIDEO_STATS: "getVideoStats",
  GET_TRENDING_VIDEOS: "getTrendingVideos",
  SEARCH_VIDEOS: "searchVideos",
  GET_USER_INTERACTIONS: "getUserVideoInteractions",
  GET_USER_TIPS: "getUserVideoTips",
  GET_VIDEO_TIPS_AGGREGATED: "getVideoTipsAggregated",
  GET_VIDEO_ANALYTICS: "getVideoAnalytics",
  GET_USER_ANALYTICS: "getUserAnalytics",
    GET_USER_LIKED_VIDEOS: "getUserLikedVideos",
  GET_USER_SAVED_VIDEOS: "getUserSavedVideos",
  GET_SINGLE_VIDEO: "getSingleVideo",
  GET_VIDEO_INTERACTIONS: "getVideoInteractions",
  GET_VIDEO_TIPS: "getVideoTips",
};

// ============ VIDEO FEED HOOKS ============

export const useGetVideoFeed = (category?: string) => {
  return useInfiniteQuery({
    queryKey: [QUERY_KEYS.GET_VIDEO_FEED, category],
    queryFn: ({ pageParam }) => {
      return getVideoFeed(category, pageParam);
    },
    getNextPageParam: (lastPage: any) => {
      // If there's no data, there are no more pages.
      if (lastPage && lastPage.documents.length === 0) {
        return null;
      }

      // Use the $id of the last document as the cursor.
      const lastId = lastPage.documents[lastPage.documents.length - 1].$id;
      return lastId;
    },
  });
};

export const useGetTrendingVideos = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_TRENDING_VIDEOS],
    queryFn: getTrendingVideos,
    staleTime: 1000 * 60 * 15,
  });
};

export const useSearchVideos = (query: string, category?: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.SEARCH_VIDEOS, query, category],
    queryFn: () => searchVideos(query, category),
    enabled: !!query && query.length > 2,
    staleTime: 1000 * 60 * 5,
  });
};

// ============ USER VIDEO HOOKS ============

export const useGetUserVideos = (userId: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_USER_VIDEOS, userId],
    queryFn: () => getUserVideos(userId),
    enabled: !!userId,
  });
};

export const useCreateVideoPost = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createVideoPost,
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_VIDEO_FEED],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_USER_VIDEOS, data.creatorId],
      });
      
      toast({
        title: "Success!",
        description: "Your video has been posted successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Upload Failed",
        description: error?.message || "Failed to post video. Please try again.",
        variant: "destructive",
      });
    },
  });
};

export const useUploadVideoFile = () => {
  return useMutation({
    mutationFn: uploadVideoFile,
    onError: (error: any) => {
      toast({
        title: "Upload Failed",
        description: error?.message || "Failed to upload video file. Please try again.",
        variant: "destructive",
      });
    },
  });
};

export const useDeleteVideoPost = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ videoId, userId }: { videoId: string; userId: string }) =>
      deleteVideoPost(videoId, userId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_VIDEO_FEED],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_USER_VIDEOS, variables.userId],
      });
      
      toast({
        title: "Deleted!",
        description: "Video has been deleted successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Delete Failed",
        description: error?.message || "Failed to delete video. Please try again.",
        variant: "destructive",
      });
    },
  });
};

// ============ VIDEO INTERACTION HOOKS ============

export const useGetVideoStats = (videoId: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_VIDEO_STATS, videoId],
    queryFn: () => getVideoStats(videoId),
    enabled: !!videoId,
    staleTime: 1000 * 30,
  });
};

export const useGetUserVideoInteractions = (userId: string, videoIds: string[]) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_USER_INTERACTIONS, userId, videoIds],
    queryFn: () => getUserVideoInteractions(userId, videoIds),
    enabled: !!userId && videoIds.length > 0,
    staleTime: 1000 * 60,
  });
};

export const useLikeVideoPost = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ videoId, userId }: { videoId: string; userId: string }) => 
      likeVideoPost(videoId, userId),
    onMutate: async ({ videoId }) => {
      await queryClient.cancelQueries({
        queryKey: [QUERY_KEYS.GET_VIDEO_STATS, videoId],
      });

      const previousStats = queryClient.getQueryData([QUERY_KEYS.GET_VIDEO_STATS, videoId]);

      queryClient.setQueryData([QUERY_KEYS.GET_VIDEO_STATS, videoId], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          likes: old.likes + 1,
        };
      });

      return { previousStats };
    },
    onError: (_error, variables, context: any) => {
      if (context?.previousStats) {
        queryClient.setQueryData(
          [QUERY_KEYS.GET_VIDEO_STATS, variables.videoId],
          context.previousStats
        );
      }
      
      toast({
        title: "Error",
        description: "Failed to update like. Please try again.",
        variant: "destructive",
      });
    },
    onSettled: (_, __, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_VIDEO_STATS, variables.videoId],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_USER_INTERACTIONS, variables.userId],
      });
    },
  });
};

export const useSaveVideoPost = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ videoId, userId }: { videoId: string; userId: string }) =>
      saveVideoPost(videoId, userId),
    onSuccess: (result: any) => {
      toast({
        title: result.isSaved ? "Saved!" : "Unsaved!",
        description: result.isSaved 
          ? "Video saved to your collection" 
          : "Video removed from your collection",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to save video. Please try again.",
        variant: "destructive",
      });
    },
    onSettled: (_, __, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_VIDEO_STATS, variables.videoId],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_USER_INTERACTIONS, variables.userId],
      });
    },
  });
};

export const useShareVideoPost = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ videoId, userId }: { videoId: string; userId: string }) =>
      shareVideoPost(videoId, userId),
    onSuccess: () => {
      toast({
        title: "Shared!",
        description: "Video shared successfully",
      });
    },
    onSettled: (_, __, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_VIDEO_STATS, variables.videoId],
      });
    },
  });
};

export const useViewVideoPost = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ videoId, userId }: { videoId: string; userId: string }) =>
      viewVideoPost(videoId, userId),
    onSettled: (_, __, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_VIDEO_STATS, variables.videoId],
      });
    },
  });
};

// ============ VIDEO TIP HOOKS ============

export const useCreateVideoTip = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createVideoTip,
    onSuccess: () => {
      toast({
        title: "Tip Initiated!",
        description: "Processing your tip payment...",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Tip Failed",
        description: error?.message || "Failed to process tip. Please try again.",
        variant: "destructive",
      });
    },
    onSettled: (data: any) => {
      if (data) {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_VIDEO_STATS, data.videoId],
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_USER_TIPS],
        });
      }
    },
  });
};

export const useUpdateVideoTipStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ 
      tipId, 
      status, 
      paymentId 
    }: { 
      tipId: string; 
      status: 'completed' | 'failed'; 
      paymentId?: string;
    }) => updateVideoTipStatus(tipId, status, paymentId),
    onSuccess: (_data: any, variables) => {
      if (variables.status === 'completed') {
        toast({
          title: "Tip Successful!",
          description: "Your tip has been sent successfully",
        });
      } else {
        toast({
          title: "Tip Failed",
          description: "Your tip could not be processed",
          variant: "destructive",
        });
      }
    },
    onSettled: (data: any) => {
      if (data) {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_VIDEO_STATS, data.videoId],
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_USER_TIPS],
        });
      }
    },
  });
};

export const useGetUserVideoTips = (userId: string, type: 'sent' | 'received') => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_USER_TIPS, userId, type],
    queryFn: () => getUserVideoTips(userId, type),
    enabled: !!userId,
  });
};

// ============ CUSTOM HOOKS FOR COMBINED FUNCTIONALITY ============

export const useVideoFeedWithInteractions = (userId: string, category?: string) => {
  const { 
    data: feedData, 
    isLoading: isFeedLoading, 
    fetchNextPage, 
    hasNextPage,
    isFetchingNextPage 
  } = useGetVideoFeed(category);

  const videoIds = feedData?.pages?.flatMap(page => 
    page.documents.map((video: any) => video.$id)
  ) || [];

  const { data: interactions } = useGetUserVideoInteractions(userId, videoIds);

  const videosWithInteractions = feedData?.pages?.map(page => ({
    ...page,
    documents: page.documents.map((video: any) => ({
      ...video,
      isLiked: interactions?.[video.$id]?.like || false,
      isSaved: interactions?.[video.$id]?.save || false,
    }))
  }));

  return {
    data: { pages: videosWithInteractions || [] },
    isLoading: isFeedLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};

export const useVideoInteractionManager = (userId: string) => {
  const likeVideo = useLikeVideoPost();
  const saveVideo = useSaveVideoPost();
  const shareVideo = useShareVideoPost();
  const viewVideo = useViewVideoPost();

  const handleLike = async (videoId: string) => {
    await likeVideo.mutateAsync({ videoId, userId });
  };

  const handleSave = async (videoId: string) => {
    await saveVideo.mutateAsync({ videoId, userId });
  };

  const handleShare = async (videoId: string) => {
    await shareVideo.mutateAsync({ videoId, userId });
  };

  const handleView = async (videoId: string) => {
    await viewVideo.mutateAsync({ videoId, userId });
  };

  return {
    handleLike,
    handleSave,
    handleShare,
    handleView,
    isLiking: likeVideo.status === 'loading',
    isSaving: saveVideo.status === 'loading', 
    isSharing: shareVideo.status === 'loading',
  };
};

// ============ BACKGROUND SYNC HOOKS ============

export const useVideoBackgroundSync = (videoId: string, userId: string) => {
  const { data: stats } = useQuery({
    queryKey: [QUERY_KEYS.GET_VIDEO_STATS, videoId],
    queryFn: () => getVideoStats(videoId),
    refetchInterval: 30000,
    refetchIntervalInBackground: true,
    enabled: !!videoId,
  });

  const { mutate: trackView } = useViewVideoPost();

  const handleVideoInView = React.useCallback(() => {
    trackView({ videoId, userId });
  }, [trackView, videoId, userId]);

  return {
    stats,
    handleVideoInView,
  };
};

export const useGetVideoAnalytics = (
  videoId: string, 
  timeframe: '7d' | '30d' | '90d' = '7d'
) => {
  return useQuery<any, Error>({
    queryKey: [QUERY_KEYS.GET_VIDEO_ANALYTICS, videoId, timeframe],
    queryFn: () => getVideoAnalytics(videoId, timeframe),
    enabled: !!videoId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useGetUserAnalytics = (
  userId: string, 
  timeframe: '7d' | '30d' | '90d' = '30d'
) => {
  return useQuery<any, Error>({
    queryKey: [QUERY_KEYS.GET_USER_ANALYTICS, userId, timeframe],
    queryFn: () => getUserAnalytics(userId, timeframe),
    enabled: !!userId,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
};

export const useGetUserLikedVideos = (userId: string) => {
  return useInfiniteQuery({
    queryKey: [QUERY_KEYS.GET_USER_LIKED_VIDEOS, userId],
    queryFn: ({ pageParam }) => getUserLikedVideos(userId, pageParam),
    getNextPageParam: (lastPage: any) => {
      if (lastPage && lastPage.documents.length === 0) return undefined;
      const lastId = lastPage.documents[lastPage.documents.length - 1].$id;
      return lastId;
    },
    enabled: !!userId,
  });
};

export const useGetUserSavedVideos = (userId: string) => {
  return useInfiniteQuery({
    queryKey: [QUERY_KEYS.GET_USER_SAVED_VIDEOS, userId],
    queryFn: ({ pageParam }) => getUserSavedVideos(userId, pageParam),
    getNextPageParam: (lastPage: any) => {
      if (lastPage && lastPage.documents.length === 0) return undefined;
      const lastId = lastPage.documents[lastPage.documents.length - 1].$id;
      return lastId;
    },
    enabled: !!userId,
  });
};

export const useGetSingleVideo = (videoId: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_SINGLE_VIDEO, videoId],
    queryFn: () => getSingleVideo(videoId),
    enabled: !!videoId,
  });
};

export const useGetVideoInteractions = (videoId: string, userId: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_VIDEO_INTERACTIONS, videoId, userId],
    queryFn: () => getVideoInteractions(videoId, userId),
    enabled: !!videoId && !!userId,
  });
};

export const useGetVideoTips = (videoId: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_VIDEO_TIPS, videoId],
    queryFn: () => getVideoTips(videoId),
    enabled: !!videoId,
    staleTime: 30000, // 30 seconds
  });
};
export const useGetCreatorTipEarnings = (creatorId: string) => {
  return useQuery({
    queryKey: ["creatorTipEarnings", creatorId],
    queryFn: async () => {
      const [pendingTips, processingTips, completedTips] = await Promise.all([
        databases.listDocuments(
          appwriteConfig.databaseId,
          appwriteConfig.videoTipsCollectionId,
          [
            Query.equal('receiverId', creatorId),
            Query.equal('status', 'pending')
          ]
        ),
        databases.listDocuments(
          appwriteConfig.databaseId,
          appwriteConfig.videoTipsCollectionId,
          [
            Query.equal('receiverId', creatorId),
            Query.equal('status', 'processing')
          ]
        ),
        databases.listDocuments(
          appwriteConfig.databaseId,
          appwriteConfig.videoTipsCollectionId,
          [
            Query.equal('receiverId', creatorId),
            Query.equal('status', 'completed')
          ]
        )
      ]);

      const pendingAmount = pendingTips.documents.reduce((sum, tip) => sum + tip.amount, 0);
      const processingAmount = processingTips.documents.reduce((sum, tip) => sum + tip.amount, 0);
      const completedAmount = completedTips.documents.reduce((sum, tip) => sum + tip.amount, 0);

      return {
        pending: { amount: pendingAmount, count: pendingTips.documents.length },
        processing: { amount: processingAmount, count: processingTips.documents.length },
        completed: { amount: completedAmount, count: completedTips.documents.length }
      };
    },
    enabled: !!creatorId,
  });
};

export const useGetCreatorPayoutHistory = (creatorId: string) => {
  return useQuery({
    queryKey: ["creatorPayoutHistory", creatorId],
    queryFn: () => databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.tipPayoutsCollectionId,
      [
        Query.equal('creator_id', creatorId),
        Query.orderDesc('created_at'),
        Query.limit(20)
      ]
    ),
    enabled: !!creatorId,
  });
};

export default {
  useGetVideoFeed,
  useGetTrendingVideos,
  useSearchVideos,
  useGetUserVideos,
  useCreateVideoPost,
  useUploadVideoFile,
  useDeleteVideoPost,
  useLikeVideoPost,
  useSaveVideoPost,
  useShareVideoPost,
  useViewVideoPost,
  useGetVideoStats,
  useGetUserVideoInteractions,
  useCreateVideoTip,
  useUpdateVideoTipStatus,
  useGetUserVideoTips,
  useVideoFeedWithInteractions,
  useVideoInteractionManager,
  useVideoBackgroundSync,
  useGetVideoAnalytics,
  useGetUserAnalytics,
  useGetUserLikedVideos,
  useGetUserSavedVideos,
  useGetSingleVideo,
  useGetVideoInteractions
};